// Copyright Materialize, Inc. and contributors. All rights reserved.
//
// Use of this software is governed by the Business Source License
// included in the LICENSE file.
//
// As of the Change Date specified in that file, in accordance with
// the Business Source License, use of this software will be governed
// by the Apache License, Version 2.0.

import * as Sentry from "@sentry/react";
import { flatGroup, greatest } from "d3";

import {
  DataPoint,
  OfflineEvent as ChartOfflineEvent,
  ReplicaData,
} from "~/platform/clusters/ClusterOverview/types";

export type OfflineEvent = {
  replicaId: string;
  occurredAt: string;
  status: string;
  reason: string | null;
};

/**
 * A replica's worst-process swap and heap limit, as fractions of one process's
 * RAM, taken from the bucket's max-heap sample.
 */
export type MemoryBreakdown = {
  swapOfRamPercent: number | null;
  /** Where RAM plus swap runs out. 1.0 means the size has no swap. */
  heapLimitPercent: number | null;
};

export type Bucket = {
  size: string | null;
  bucketStart: Date;
  replicaId: string;
  bucketEnd: Date;
  name: string;
  // The cluster ID of the replica's current blue-green deployment
  currentDeploymentClusterId: string;
  // The cluster ID of the replica. If the cluster was dropped,
  // this will be different from currentDeploymentClusterId
  clusterId: string;
  maxMemory: {
    percent: number | null;
    occurredAt: Date;
  };
  maxDisk: {
    percent: number | null;
    occurredAt: Date;
  };
  maxCpu: {
    percent: number | null;
    occurredAt: Date;
  };
  maxHeap: {
    percent: number | null;
    occurredAt: Date;
  };
  maxMemoryAndDisk: {
    memoryPercent: number | null;
    diskPercent: number | null;
    percent: number | null;
    occurredAt: Date;
  };
  /** Absent unless the query selected the swap columns. */
  memoryBreakdown?: MemoryBreakdown;

  offlineEvents: OfflineEvent[] | null;
};

export interface UtilizationSample {
  replicaId: string;
  clusterId: string | null;
  size: string | null;
  name: string | null;
  occurredAt: Date;
  cpuPercent: number | null;
  memoryPercent: number | null;
  diskPercent: number | null;
  heapPercent: number | null;
  memoryAndDiskPercent: number | null;
  swapOfRamPercent?: number | null;
  heapLimitPercent?: number | null;
}

/** A (replica, bucket) rollup, matching the binned `_overview*` views' output. */
export interface UtilizationBucketRow {
  bucketStart: Date;
  bucketEnd: Date;
  replicaId: string;
  clusterId: string | null;
  size: string | null;
  name: string | null;
  maxMemoryPercent: number | null;
  maxMemoryAt: Date;
  maxDiskPercent: number | null;
  maxDiskAt: Date;
  maxCpuPercent: number | null;
  maxCpuAt: Date;
  maxHeapPercent: number | null;
  maxHeapAt: Date;
  maxMemoryAndDiskPercent: number | null;
  maxMemoryAndDiskMemoryPercent: number | null;
  maxMemoryAndDiskDiskPercent: number | null;
  maxMemoryAndDiskAt: Date;
  offlineEvents: OfflineEvent[] | null;
  swapOfRamPercent?: number | null;
  heapLimitPercent?: number | null;
}

/**
 * Raw row from the binned 24h SUBSCRIBE, where the date columns arrive as ISO
 * strings rather than `Date`s.
 */
export type BinnedSubscribeRow = Omit<
  UtilizationBucketRow,
  | "bucketStart"
  | "bucketEnd"
  | "maxMemoryAt"
  | "maxDiskAt"
  | "maxCpuAt"
  | "maxHeapAt"
  | "maxMemoryAndDiskAt"
> & {
  bucketStart: string;
  bucketEnd: string;
  maxMemoryAt: string;
  maxDiskAt: string;
  maxCpuAt: string;
  maxHeapAt: string;
  maxMemoryAndDiskAt: string;
};

/** Parse a binned 24h SUBSCRIBE row's string dates into a `UtilizationBucketRow`. */
export function parseBinnedSubscribeRow(
  raw: BinnedSubscribeRow,
): UtilizationBucketRow {
  return {
    ...raw,
    bucketStart: new Date(raw.bucketStart),
    bucketEnd: new Date(raw.bucketEnd),
    maxMemoryAt: new Date(raw.maxMemoryAt),
    maxDiskAt: new Date(raw.maxDiskAt),
    maxCpuAt: new Date(raw.maxCpuAt),
    maxHeapAt: new Date(raw.maxHeapAt),
    maxMemoryAndDiskAt: new Date(raw.maxMemoryAndDiskAt),
  };
}

/** argmax over samples by a metric, treating null as the lowest value. */
export function maxByMetric(
  samples: UtilizationSample[],
  metric: (s: UtilizationSample) => number | null,
): UtilizationSample {
  return (
    greatest(samples, (s) => metric(s) ?? Number.NEGATIVE_INFINITY) ??
    samples[0]
  );
}

/**
 * Bins raw samples into per-(replica, bucket) maxima, client-side. Buckets are
 * epoch-aligned to match the SQL `date_bin` 1970 origin. Offline events aren't in
 * the un-binned base, so they are null here.
 */
export function rebucketUtilizationSamples(
  samples: UtilizationSample[],
  bucketSizeMs: number,
  startDateMs: number,
): UtilizationBucketRow[] {
  const bucketStartOf = (s: UtilizationSample) =>
    Math.floor(s.occurredAt.getTime() / bucketSizeMs) * bucketSizeMs;

  // The 3h base covers more than shorter windows (e.g. "Last hour"); clip to the
  // requested window so the chart doesn't show extra history.
  const inWindow = samples.filter((s) => s.occurredAt.getTime() >= startDateMs);

  const rows = flatGroup(inWindow, (s) => s.replicaId, bucketStartOf).map(
    ([replicaId, bucketStartMs, group]): UtilizationBucketRow => {
      const first = group[0];
      const cpu = maxByMetric(group, (s) => s.cpuPercent);
      const memory = maxByMetric(group, (s) => s.memoryPercent);
      const disk = maxByMetric(group, (s) => s.diskPercent);
      const heap = maxByMetric(group, (s) => s.heapPercent);
      const memoryAndDisk = maxByMetric(group, (s) => s.memoryAndDiskPercent);
      return {
        bucketStart: new Date(bucketStartMs),
        bucketEnd: new Date(bucketStartMs + bucketSizeMs),
        replicaId,
        clusterId: first.clusterId,
        size: first.size,
        name: first.name,
        maxMemoryPercent: memory.memoryPercent,
        maxMemoryAt: memory.occurredAt,
        maxDiskPercent: disk.diskPercent,
        maxDiskAt: disk.occurredAt,
        maxCpuPercent: cpu.cpuPercent,
        maxCpuAt: cpu.occurredAt,
        maxHeapPercent: heap.heapPercent,
        maxHeapAt: heap.occurredAt,
        maxMemoryAndDiskPercent: memoryAndDisk.memoryAndDiskPercent,
        maxMemoryAndDiskMemoryPercent: memoryAndDisk.memoryPercent,
        maxMemoryAndDiskDiskPercent: memoryAndDisk.diskPercent,
        maxMemoryAndDiskAt: memoryAndDisk.occurredAt,
        offlineEvents: null,
        // Take swap from the max-heap sample, as the binned views do.
        swapOfRamPercent: heap.swapOfRamPercent,
        heapLimitPercent: heap.heapLimitPercent,
      };
    },
  );

  rows.sort((a, b) => a.bucketStart.getTime() - b.bucketStart.getTime());
  return rows;
}

/**
 * Attach offline events to their (replica, bucket) rows. The un-binned 3h base
 * carries no status columns, so the <=3h tier fetches events separately and
 * merges them here. Events with no matching bucket are dropped, like the
 * binned views' LEFT JOIN from the metrics-derived buckets.
 */
export function attachOfflineEvents(
  rows: UtilizationBucketRow[],
  events: OfflineEvent[],
  bucketSizeMs: number,
): UtilizationBucketRow[] {
  if (events.length === 0) return rows;
  const eventsByBucket = new Map<string, OfflineEvent[]>();
  for (const event of events) {
    const bucketStartMs =
      Math.floor(new Date(event.occurredAt).getTime() / bucketSizeMs) *
      bucketSizeMs;
    const key = `${event.replicaId} ${bucketStartMs}`;
    const bucketEvents = eventsByBucket.get(key) ?? [];
    bucketEvents.push(event);
    eventsByBucket.set(key, bucketEvents);
  }
  return rows.map((row) => {
    const bucketEvents = eventsByBucket.get(
      `${row.replicaId} ${row.bucketStart.getTime()}`,
    );
    return bucketEvents ? { ...row, offlineEvents: bucketEvents } : row;
  });
}

/**
 * Group per-(replica, bucket) rows into `Bucket[]` by replica, tracking the
 * overall min/max bounds. `resolveCurrentDeployment` maps a past cluster id to its
 * current blue-green deployment; omitted, it defaults to the row's own `clusterId`.
 */
export function bucketRowsToBucketsByReplicaId(
  rows: UtilizationBucketRow[],
  resolveCurrentDeployment?: (clusterId: string) => string | undefined,
): {
  bucketsByReplicaId: Record<string, Bucket[]>;
  minBucketStartMs: number;
  maxBucketEndMs: number;
} {
  const bucketsByReplicaId: Record<string, Bucket[]> = {};

  let minBucketStartMs = Number.POSITIVE_INFINITY;
  let maxBucketEndMs = Number.NEGATIVE_INFINITY;

  for (const row of rows) {
    minBucketStartMs = Math.min(minBucketStartMs, row.bucketStart.getTime());
    maxBucketEndMs = Math.max(maxBucketEndMs, row.bucketEnd.getTime());

    const {
      replicaId,
      size,
      bucketStart,
      bucketEnd,
      name,
      clusterId,
      offlineEvents,
    } = row;

    const buckets = bucketsByReplicaId[replicaId];

    if (name === null || clusterId === null) {
      const err = new Error(
        `Expected name: ${name} and clusterId: ${clusterId} to be defined`,
      );

      Sentry.captureException(err);
      throw err;
    }

    const currentDeploymentClusterId =
      resolveCurrentDeployment?.(clusterId) ?? clusterId;

    const newBucket: Bucket = {
      size,
      bucketStart,
      bucketEnd,
      offlineEvents,
      name,
      currentDeploymentClusterId,
      clusterId,
      replicaId,
      maxMemory: {
        percent: row.maxMemoryPercent,
        occurredAt: row.maxMemoryAt,
      },
      maxDisk: {
        percent: row.maxDiskPercent,
        occurredAt: row.maxDiskAt,
      },
      maxCpu: {
        percent: row.maxCpuPercent,
        occurredAt: row.maxCpuAt,
      },
      maxHeap: {
        percent: row.maxHeapPercent ?? null,
        occurredAt: row.maxHeapAt ?? new Date(),
      },
      maxMemoryAndDisk: {
        percent: row.maxMemoryAndDiskPercent,
        memoryPercent: row.maxMemoryAndDiskMemoryPercent,
        diskPercent: row.maxMemoryAndDiskDiskPercent,
        occurredAt: row.maxMemoryAndDiskAt,
      },
      memoryBreakdown:
        row.heapLimitPercent === undefined
          ? undefined
          : {
              swapOfRamPercent: row.swapOfRamPercent ?? null,
              heapLimitPercent: row.heapLimitPercent,
            },
    };

    if (buckets) {
      buckets.push(newBucket);
    } else {
      bucketsByReplicaId[replicaId] = [newBucket];
    }
  }

  return {
    minBucketStartMs,
    maxBucketEndMs,
    bucketsByReplicaId,
  };
}

// Rescales shares of RAM to percentages of the heap limit, `heapPercent`'s
// scale. Null checks, not truthiness: 0% swap is a reading, not a gap.
const toHeapLimitPercents = (breakdown: MemoryBreakdown | undefined) => {
  if (!breakdown?.heapLimitPercent) {
    return { swapPercent: null, ramLimitPercent: null };
  }
  const heapLimit = breakdown.heapLimitPercent;
  return {
    swapPercent:
      breakdown.swapOfRamPercent === null
        ? null
        : (breakdown.swapOfRamPercent / heapLimit) * 100,
    ramLimitPercent: 100 / heapLimit,
  };
};

/**
 * Shape `bucketsByReplicaId` into the chart's per-replica series plus the flat
 * offline-event list, clamping the axis to the data bounds. Shared by the
 * polling and SUBSCRIBE paths so both render identically.
 */
export function toReplicaUtilizationGraphData(
  data: {
    bucketsByReplicaId: Record<string, Bucket[]>;
    minBucketStartMs: number;
    maxBucketEndMs: number;
  },
  startDate: Date,
  endDate: Date,
) {
  const graphData = Object.entries(data.bucketsByReplicaId).map(
    ([replicaId, replicaData]) => {
      return {
        id: replicaId,
        data: replicaData.map(
          ({
            bucketEnd,
            bucketStart,
            maxHeap,
            maxMemory,
            maxCpu,
            maxDisk,
            maxMemoryAndDisk,
            memoryBreakdown,
            size,
            offlineEvents,
            name,
            clusterId,
          }) => ({
            id: replicaId,
            name,
            clusterId,
            bucketEnd: bucketEnd.getTime(),
            bucketStart: bucketStart.getTime(),
            cpuPercent: maxCpu?.percent ? maxCpu.percent * 100 : null,
            diskPercent: maxDisk?.percent ? maxDisk.percent * 100 : null,
            memoryPercent: maxMemory?.percent ? maxMemory.percent * 100 : null,
            // Memory utilization calculated in SQL: (memory_bytes + disk_bytes) / (available_memory + available_disk)
            maxMemoryAndDiskPercent: maxMemoryAndDisk?.percent
              ? maxMemoryAndDisk.percent * 100
              : null,
            heapPercent: maxHeap?.percent ? maxHeap.percent * 100 : null,
            ...toHeapLimitPercents(memoryBreakdown),
            size,
            offlineEvents:
              offlineEvents?.map((event) => ({
                id: event.replicaId,
                offlineReason: event.reason,
                status: event.status,
                timestamp: new Date(event.occurredAt).getTime(),
              })) ?? [],
          }),
        ),
      };
    },
  );

  const offlineEvents: Array<ChartOfflineEvent> = [];

  for (const replica of graphData) {
    for (const replicaDatum of replica.data) {
      for (const {
        status,
        offlineReason,
        timestamp,
      } of replicaDatum.offlineEvents) {
        if (
          (status === "not-ready" || status === "offline") &&
          offlineReason !== "oom-killed"
        ) {
          offlineEvents.push({
            id: replicaDatum.id,
            offlineReason,
            status,
            timestamp,
          });
        }
      }
    }
  }

  // Widen the axis to cover both the selected window and the actual data bounds,
  // so edge buckets aren't clipped and a short history still fills the range.
  const clampedStartDate = new Date(
    Math.min(startDate.getTime(), data.minBucketStartMs),
  );
  const clampedEndDate = new Date(
    Math.max(endDate.getTime(), data.maxBucketEndMs),
  );

  return {
    startDate: clampedStartDate,
    endDate: clampedEndDate,
    graphData,
    offlineEvents,
  };
}

/** Whether the point has a non-zero CPU, memory or disk reading. */
export const hasUtilizationReading = (point: DataPoint) =>
  Boolean(point.cpuPercent || point.memoryPercent || point.diskPercent);

/**
 * Whether every replica's CPU, memory and disk readings are empty or zero,
 * which points at broken metrics collection rather than idle replicas.
 */
export const hasNoUtilizationMetrics = (graphData: ReplicaData[]) =>
  graphData.length > 0 &&
  graphData.every(({ data }) => !data.some(hasUtilizationReading));

/**
 * Server-binned rows inside a window that starts at `startDate`, in time order.
 * Held rows from a wider window would otherwise stretch the chart past the
 * selected range, and ENVELOPE UPSERT yields them unordered.
 */
export function clipBinnedRowsToWindow<T extends { bucketStart: Date }>(
  rows: T[],
  startDate: Date,
): T[] {
  return rows
    .filter((row) => row.bucketStart.getTime() >= startDate.getTime())
    .sort((a, b) => a.bucketStart.getTime() - b.bucketStart.getTime());
}
